import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import Brands from "../brands/Brands";
import Category from "../category/Category";
import Batches from "../batches/Batches";
import Purchases from "../purchases/Purchases";
import ProductTab from "./ProductTab";
import { productAPI } from "../../services";
import StockEditIcon from "../../assets/icons/StockEditIcon";
import { useSelector } from "react-redux";
import "../../style/productDetail.css";

import BackIcon from "../../assets/icons/BackIcon";
import NoStock from "../../assets/no-stock.svg";

const ProductDetails = ({
  activeLabel = "products",
  setActiveLabel = () => {},
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingLowStock, setIsEditingLowStock] = useState(false);
  const [lowStockValue, setLowStockValue] = useState("");
  const [savingLowStock, setSavingLowStock] = useState(false);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  // Ref to prevent duplicate API calls
  const loadingRef = useRef(false);

  useEffect(() => {
    const loadProduct = async () => {
      // Prevent duplicate calls
      if (loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        setLoading(true);
        const response = await productAPI.getProductDetail(id);
        // Handle nested API response structure
        const productData = response.data || response;
        setProduct(productData);
      } catch (error) {
        console.error("Error loading product:", error);

        // Fallback to localStorage for development
        const storedProducts = localStorage.getItem("products");
        if (storedProducts) {
          try {
            const parsedProducts = JSON.parse(storedProducts);
            const foundProduct = parsedProducts.find((p) => p.id === id);
            setProduct(foundProduct);
          } catch (error) {
            console.error("Error loading product from localStorage:", error);
          }
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    if (id) {
      loadProduct();
    }
  }, [id]);

  const getStatusBadge = (product) => {
    // Use API status if available, otherwise determine based on stock level
    if (product.status) {
      const status = product.status;
      let className;

      if (status === "Out of Stock") {
        className = "status-badge-red";
      } else if (status === "Low Stock") {
        className = "status-badge-orange";
      } else {
        className = "status-badge-green";
      }

      return <span className={`status-badge ${className}`}>{status}</span>;
    }

    // Fallback to stock-based logic
    const stock = product.in_stock || product.stock || 0;
    let status, className;

    if (stock === 0) {
      status = "Out of Stock";
      className = "status-badge-red";
    } else if (stock <= 5) {
      status = "Low Stock";
      className = "status-badge-orange";
    } else {
      status = "In Stock";
      className = "status-badge-green";
    }

    return <span className={`status-badge ${className}`}>{status}</span>;
  };

  const handleBackToProducts = () => {
    navigate("/products");
  };

  const handleEditLowStock = () => {
    setLowStockValue(product.minimum_required_stock || 0);
    setIsEditingLowStock(true);
  };

  const handleCancelLowStock = () => {
    setIsEditingLowStock(false);
    setLowStockValue("");
  };

  const handleSaveLowStock = async () => {
    try {
      setSavingLowStock(true);
      const minimumStock = parseInt(lowStockValue, 10);

      if (isNaN(minimumStock) || minimumStock < 0) {
        toast.error("Please enter a valid number");
        return;
      }

      let response;
      if (product.low_stock_id) {
        // Update existing low stock threshold
        response = await productAPI.updateLowStockThreshold(
          product.low_stock_id,
          minimumStock
        );
      } else {
        // Add new low stock threshold
        response = await productAPI.addLowStockThreshold(
          product.id,
          minimumStock
        );
      }

      // Update product state with new values
      setProduct((prevProduct) => ({
        ...prevProduct,
        minimum_required_stock: minimumStock,
        low_stock_id:
          response.data?.low_stock_id ||
          response.data?.id ||
          prevProduct.low_stock_id,
      }));

      setIsEditingLowStock(false);
      toast.success("Low stock threshold updated successfully!");
    } catch (error) {
      console.error("Error saving low stock threshold:", error);
      toast.error("Failed to update low stock threshold. Please try again.");
    } finally {
      setSavingLowStock(false);
    }
  };

  // Handle header navigation - switch tabs within product details
  const handleHeaderNavigation = (tabValue) => {
    setActiveLabel(tabValue);
  };

  // Override the onHeaderNavigation prop to use our internal handler
  const overrideProps = {
    activeLabel,
    setActiveLabel,
    onHeaderNavigation: handleHeaderNavigation,
  };

  const renderContent = () => {
    switch (activeLabel) {
      case "products":
        return (
          <div className="product-detail-page">
            <div className="product-detail-container">
              <div className="back-navigation">
                <button className={`${isPartsURL ? "back-button-parts" : isAccessoriesURL ? "back-button-acc" : "back-button"}`} onClick={handleBackToProducts}>
                  <BackIcon fill={isPartsURL ? "#DB2E31" : isAccessoriesURL ? "#72BF48" : "#2F80ED"} />
                  Back to Products
                </button>
              </div>

              <div className="product-detail-content">
                <div className="page-header">
                  <h1 className="page-title">Product Details</h1>
                  <p className="page-subtitle">
                    Complete information about {getProductName(product)}
                  </p>
                </div>

                <div className="product-overview-card">
                  <div className="product-header">
                    <div className="product-title-section">
                      <h2 className="product-name-detail">
                        {getProductName(product)}
                      </h2>
                      {getStatusBadge(product)}
                    </div>
                    <div className="product-specs">
                      <span className="spec-item">
                        Category:{" "}
                        {product.category_name || product.category || "--"}
                        {product.ram && ` • RAM: ${product.ram}`}
                        {product.storage && ` • Storage: ${product.storage}`}
                      </span>
                    </div>
                  </div>

                  <div className="product-metrics">
                    <div className="metric-item">
                      <div className={`metric-value ${isPartsURL ? "metric-value-blue-parts" : isAccessoriesURL ? "metric-value-blue-acc" : "metric-value-blue"}`}>
                        €
                        {parseFloat(
                          product.avg_buying_price || product.buyPrice || 0
                        ).toFixed(2)}
                      </div>
                      <div className="metric-label">Buying Price</div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-value">
                        €
                        {parseFloat(
                          product.avg_selling_price || product.sellPrice || 0
                        ).toFixed(2)}
                      </div>
                      <div className="metric-label">Selling Price</div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-value">
                        {product.in_stock || product.stock || 0}
                      </div>
                      <div className="metric-label">In Stock</div>
                    </div>
                    {/* Low Stock Threshold */}
                    <div className="metric-item">
                      {isEditingLowStock ? (
                        <div
                          className="metric-value"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <input
                            type="number"
                            value={lowStockValue}
                            onChange={(e) => setLowStockValue(e.target.value)}
                            className="form-control"
                            style={{
                              width: "80px",
                              height: "32px",
                              fontSize: "14px",
                              boxShadow: "none",
                              border: `${isPartsURL ? "1px solid #DB2E31" : isAccessoriesURL ? "1px solid #72BF48" : "1px solid #D1D5DB"}`,
                            }}
                            min="0"
                            autoFocus
                            disabled={savingLowStock}
                          />
                          <button
                            onClick={handleSaveLowStock}
                            className="btn btn-sm btn-success"
                            style={{ padding: "4px 8px", fontSize: "12px" }}
                            disabled={savingLowStock}
                          >
                            {savingLowStock ? "..." : "✓"}
                          </button>
                          <button
                            onClick={handleCancelLowStock}
                            className="btn btn-sm btn-secondary"
                            style={{ padding: "4px 8px", fontSize: "12px" }}
                            disabled={savingLowStock}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div
                          className="metric-value"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {product.minimum_required_stock || 0}
                          <span
                            onClick={handleEditLowStock}
                            style={{ cursor: "pointer" }}
                          >
                            <StockEditIcon fill={isPartsURL ? "#DB2E31" : isAccessoriesURL ? "#72BF48" : "#2F80ED"} />
                          </span>
                        </div>
                      )}
                      <div className="metric-label">Low Stock Threshold</div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-value">
                        €{totalValue.toFixed(2)}
                      </div>
                      <div className="metric-label">Total Value</div>
                    </div>
                  </div>
                </div>

                <div className="variant-features-card">
                  <h3 className="card-title">Variant Features</h3>
                  <div className="variant-sections">
                    {renderVariantFeatures(product)}
                  </div>
                </div>

                <div className="stock-history-card">
                  <h3 className="card-title">Stock History</h3>
                  <div className="no-data-container">
                    <div className="no-data-icon">
                      <img src={NoStock} alt="No Stock" />
                    </div>
                    <div className="no-data-text">
                      No stock history available for this product.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "brands":
        return <Brands {...overrideProps} />;
      case "categories":
        return <Category {...overrideProps} />;
      case "batches":
        return <Batches {...overrideProps} />;
      case "purchases":
        return <Purchases {...overrideProps} />;
      default:
        return <ProductTab {...overrideProps} />;
    }
  };

  const getProductName = (product) => {
    // Handle API response structure
    const brandName = product.brand_name || product.brandName;
    const modelName = product.model_name || product.modelName || product.name;

    if (brandName && modelName) {
      return `${brandName} ${modelName}`;
    }

    return modelName || product.name || "Unnamed Product";
  };

  const renderVariantFeatures = (product) => {
    const variants = product.variants || product.variantFeatures || [];
    const renderedFeatures = [];

    // Handle new API structure where variants are objects with direct properties
    if (variants.length > 0) {
      variants.forEach((variant, index) => {
        Object.entries(variant).forEach(([key, value]) => {
          if (value) {
            const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

            if (key.toLowerCase() === "color") {
              renderedFeatures.push(
                <div key={`${key}-${index}`} className="variant-section">
                  <div className="variant-label">{capitalizedKey}</div>
                  <div className="color-option">
                    <div
                      className={`color-circle color-${value.toLowerCase()}`}
                    ></div>
                    <span className="color-name">{value}</span>
                  </div>
                </div>
              );
            } else {
              renderedFeatures.push(
                <div key={`${key}-${index}`} className="variant-section">
                  <div className="variant-label">{capitalizedKey}</div>
                  <div className="variant-value">{value}</div>
                </div>
              );
            }
          }
        });
      });
    }

    // Handle old structure with name/value pairs
    if (renderedFeatures.length === 0 && variants.length > 0) {
      variants.forEach((variant, index) => {
        if (variant.name && variant.value) {
          const capitalizedName =
            variant.name.charAt(0).toUpperCase() + variant.name.slice(1);

          if (variant.name.toLowerCase().includes("color")) {
            renderedFeatures.push(
              <div key={`${variant.name}-${index}`} className="variant-section">
                <div className="variant-label">{capitalizedName}</div>
                <div className="color-option">
                  <div
                    className={`color-circle color-${variant.value.toLowerCase()}`}
                  ></div>
                  <span className="color-name">{variant.value}</span>
                </div>
              </div>
            );
          } else {
            renderedFeatures.push(
              <div key={`${variant.name}-${index}`} className="variant-section">
                <div className="variant-label">{capitalizedName}</div>
                <div className="variant-value">{variant.value}</div>
              </div>
            );
          }
        }
      });
    }

    // Fallback: show basic product properties if no variants
    if (renderedFeatures.length === 0) {
      if (product.storage) {
        renderedFeatures.push(
          <div key="storage" className="variant-section">
            <div className="variant-label">Storage</div>
            <div className="variant-value">{product.storage}</div>
          </div>
        );
      }
      if (product.ram) {
        renderedFeatures.push(
          <div key="ram" className="variant-section">
            <div className="variant-label">RAM</div>
            <div className="variant-value">{product.ram}</div>
          </div>
        );
      }
    }

    return renderedFeatures.length > 0 ? (
      renderedFeatures
    ) : (
      <div className="no-data-text">No variant features available</div>
    );
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="card shadow-sm rounded-3 p-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <AlertCircle size={48} className="text-muted mb-3" />
            <h4 className="text-muted">Product not found</h4>
            <p className="text-muted">
              The product you're looking for doesn't exist.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/products")}
            >
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalValue =
    (product.avg_buying_price || product.buyPrice || 0) *
    (product.in_stock || product.stock || 0);

  return renderContent();
};

export default ProductDetails;
